import {
  DropdownMenuItem,
  SnapSelectMenuOption,
} from "@snap-mobile/snap-ui/dist/types/utils";
import { ITEMS_PER_PAGE } from "../../constants";
import { SpendCounterparty } from "graphql/generated";
import { useAppContext } from "hooks/use-context";
import useModal from "hooks/use-modal";
import React, { SetStateAction, useState } from "react";
import {
  SnapButton,
  SnapDropDown,
  SnapPagination,
  SnapSelectMenu,
  SnapTable,
} from "suit";
import { Divider } from "components";
import DeleteCounterparty from "./delete-counterparty";
import EinDisplay from "components/ein-display";

type ManageCounterpartiesProps = {
  fromOptions: SnapSelectMenuOption[];
  setFromOptions: React.Dispatch<SetStateAction<SnapSelectMenuOption[]>>;
  selectedFrom: SnapSelectMenuOption | undefined;
  setSelectedFrom: React.Dispatch<
    React.SetStateAction<SnapSelectMenuOption | undefined>
  >;
  setSelectedTab: React.Dispatch<React.SetStateAction<number>>;
  counterparties: SpendCounterparty[];
};

function ManageCounterparties({
  fromOptions,
  setFromOptions,
  selectedFrom,
  setSelectedFrom,
  setSelectedTab,
  counterparties,
}: ManageCounterpartiesProps) {
  const { screenSize, toast } = useAppContext();
  const { isOpen: isDeleteOpen, toggle: toggleDelete } = useModal();
  const [page, setPage] = useState(0);
  const [selectedCounterparty, setSelectedCounterparty] =
    useState<SpendCounterparty>();
  const [options] = useState<DropdownMenuItem[]>([
    {
      text: "Delete",
      name: "Delete",
      value: "Delete",
    },
  ]);
  return (
    <>
      <div className="lg:grid grid-cols-2">
        <SnapSelectMenu
          label="From Account"
          placeholder="Select Account"
          selectMenuOptions={fromOptions}
          onSnap-select-menu-updated={(e) => {
            const selectedItem = e.detail.find((item) => item.selected);
            setSelectedFrom(selectedItem);
            setFromOptions(e.detail);
          }}
        />
        <div className="flex justify-end items-end lg:mt-0 mt-4">
          <SnapButton
            variant="primary"
            className="lg:w-36 w-full mb-6 lg:mb-0"
            size="sm"
            fullWidth
            onClick={() => {
              if (!selectedFrom?.value) {
                toast?.setToast({
                  message: "Please select an account before adding payee.",
                  type: "danger",
                });
              } else {
                setSelectedTab(2);
              }
            }}
          >
            Add Payee
          </SnapButton>
        </div>
      </div>
      {screenSize?.isDesktop && (
        <SnapTable>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Bank</th>
                <th>Routing #</th>
                <th>Account #</th>
                <th>EIN</th>
                <th>{/* Action */}</th>
              </tr>
            </thead>
            <tbody>
              {counterparties
                .slice(
                  page * ITEMS_PER_PAGE,
                  page * ITEMS_PER_PAGE + ITEMS_PER_PAGE
                )
                .map((counterparty) => {
                  return (
                    <tr key={counterparty.id}>
                      <td>{counterparty.name}</td>
                      <td>{counterparty.bankName}</td>
                      <td>{counterparty.routingNumber}</td>
                      <td className="flex justify-center items-center">
                        **{counterparty.accountLastFour}
                      </td>
                      <td>
                        {counterparty.einNumber && (
                          <EinDisplay
                            id={counterparty.id}
                            type={"counterparty"}
                          />
                        )}
                      </td>
                      <td>
                        <SnapDropDown
                          minimal
                          options={options}
                          onSnap-dropdown-item-selected={(e) => {
                            if (e.detail.value === "Delete") {
                              toggleDelete();
                            }
                            setSelectedCounterparty(counterparty);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </SnapTable>
      )}
      {screenSize?.isMobile &&
        counterparties
          .slice(page * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE + ITEMS_PER_PAGE)
          .map((counterparty) => {
            return (
              <div key={counterparty.id} className="border p-4 rounded-lg mt-4">
                <div className="flex justify-between">
                  <p className="font-medium">{counterparty.name}</p>
                  <SnapDropDown
                    buttonText="Options"
                    modalType="drawer"
                    minimal
                    options={[
                      {
                        name: "Delete",
                        text: "Delete",
                        value: "Delete",
                        selected: false,
                      },
                    ]}
                    onSnap-dropdown-item-selected={(e) => {
                      if (e.detail.value === "Delete") {
                        toggleDelete();
                      }
                      setSelectedCounterparty(counterparty);
                    }}
                  />
                </div>
                <Divider isVisibleOnMobile />
                <div className="grid grid-cols-4 mt-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-500">Bank</p>
                    <p className="text-sm">{counterparty.bankName}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500">
                      Account Number
                    </p>
                    <p className="text-sm">{counterparty.accountLastFour}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500">
                      Routing Number
                    </p>
                    <p className="text-sm">{counterparty.routingNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500">EIN</p>
                    {counterparty.einNumber && (
                      <EinDisplay id={counterparty.id} type={"counterparty"} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      <SnapPagination
        itemCount={counterparties.length}
        currentPage={page}
        pageSize={ITEMS_PER_PAGE}
        onSnap-pagination-page-changed={(e) => {
          setPage(e.detail);
        }}
      />
      {isDeleteOpen && (
        <DeleteCounterparty
          isDeleteOpen={isDeleteOpen}
          toggleDelete={toggleDelete}
          selectedCounterparty={selectedCounterparty}
          selectedFrom={selectedFrom}
        />
      )}
    </>
  );
}

export default ManageCounterparties;
